<template>
  <div class="bind-modal">
    <div class="bind-modal__stack">
      <section class="bind-modal__panel bind-modal__panel--top">
        <button class="bind-modal__close" type="button" @click="handleClose">
          <i data-lucide="x"></i>
        </button>

        <h3 class="bind-modal__title">{{ $t("login.googleAuthBinding") }}</h3>

        <div class="bind-modal__desc">
          <span>{{ $t("login.bindingModule.description") }}</span>
          <button
            class="bind-modal__help"
            type="button"
            @click="openGoogleCode"
          >
            {{ $t("login.howToUseAuthenticator") }}
          </button>
        </div>

        <div class="bind-modal__secret">
          <div class="bind-modal__qr-shell">
            <div v-if="qrLoading" class="bind-modal__qr-state">
              <i data-lucide="loader-2" class="bind-modal__qr-loading"></i>
            </div>
            <button
              v-else-if="qrError"
              class="bind-modal__qr-state bind-modal__qr-retry"
              type="button"
              @click="getQRcode"
            >
              重新加载
            </button>
            <img
              v-else-if="newSrc"
              :src="newSrc"
              alt="QR Code"
              class="bind-modal__qr"
              @error="handleQrLoadError"
            />
            <div v-else class="bind-modal__qr-state"></div>
          </div>
          <div class="bind-modal__secret-body">
            <div class="bind-modal__secret-label">
              {{ $t("login.bindingModule.activationCodeLabel") }}
            </div>
            <div class="bind-modal__secret-code">
              <span>{{ formattedSecretKey }}</span>
            </div>
          </div>
        </div>

        <div class="bind-modal__steps">
          <article class="bind-step">
            <div class="bind-step__head">
              <span class="bind-step__index">01</span>
              <span class="bind-step__text">
                {{ bindStepText1Line1 }}<br />{{ bindStepText1Line2 }}
              </span>
            </div>
            <div class="bind-step__body">
              <div class="bind-step__scan">
                <i data-lucide="scan" class="bind-step__scan-icon"></i>
                <div class="bind-step__scan-bar"></div>
                <div class="bind-step__scan-line"></div>
              </div>
            </div>
          </article>

          <article class="bind-step">
            <div class="bind-step__head">
              <span class="bind-step__index">02</span>
              <span class="bind-step__text">
                查看验证器生成的<span class="bind-step__text-strong"
                  >6位动态数字</span
                >，<br />
                输入到底部验证码框中
              </span>
            </div>
            <div class="bind-step__body">
              <div class="bind-token">
                <div class="bind-token__top">
                  <span>Graphchain</span>
                  <i data-lucide="more-horizontal" class="bind-token__more"></i>
                </div>
                <div class="bind-token__code">
                  603902
                  <i data-lucide="copy" class="bind-token__copy"></i>
                </div>
                <div class="bind-token__progress">
                  <div class="bind-token__progress-bar"></div>
                </div>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section class="bind-modal__panel bind-modal__panel--bottom">
        <div class="bind-modal__input-wrap">
          <el-input
            v-model="verCode"
            maxlength="6"
            :placeholder="$t('login.bindingModule.verification.placeholder')"
            @keyup.enter.native="sureverCode"
          >
          </el-input>
          <span
            v-if="verCode"
            class="bind-modal__input-clear"
            @click.stop="verCode = ''"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path
                d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z"
              />
            </svg>
          </span>
        </div>
        <button
          class="bind-modal__confirm"
          type="button"
          :disabled="!verCode || verCode.length !== 6"
          @click="sureverCode"
        >
          绑定
        </button>
      </section>
    </div>
  </div>
</template>

<script>
import { GoogleQr, bindingverCode } from "@/aips/login";
import {
  clearLocalStorageExceptUserGuides,
  getAccessTokenFromAuthData,
} from "@/utils/storageHelper.js";

export default {
  name: "GoogleBindingDialog",
  props: ["googleform", "handleClose1"],
  data() {
    return {
      newSrc: "",
      verCode: "",
      qrLoading: false,
      qrError: false,
      qrObjectUrl: "",
      qrRequestSeq: 0,
    };
  },
  computed: {
    formattedSecretKey() {
      const secretKey = this.googleform && this.googleform.secretKey;
      if (!secretKey) return "--";
      return secretKey.replace(/(.{4})/g, "$1 ").trim();
    },
    bindStepText1Line1() {
      return "打开身份验证器App，";
    },
    bindStepText1Line2() {
      return "扫描上方二维码或输入激活码";
    },
  },
  watch: {
    googleform: {
      handler() {
        this.verCode = "";
        this.getQRcode();
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    this.reloadIcons();
  },
  updated() {
    this.reloadIcons();
  },
  beforeDestroy() {
    this.revokeQrObjectUrl();
  },
  methods: {
    reloadIcons() {
      this.$nextTick(() => {
        if (window.lucide) {
          window.lucide.createIcons({ attrs: { "stroke-width": "1.8" } });
        }
      });
    },
    openGoogleCode() {
      this.$emit("openGoogleCode");
    },
    handleClose() {
      this.$emit("handleClose1");
    },
    revokeQrObjectUrl() {
      if (this.qrObjectUrl && window.URL) {
        window.URL.revokeObjectURL(this.qrObjectUrl);
      }
      this.qrObjectUrl = "";
    },
    async getQRcode() {
      const requestSeq = ++this.qrRequestSeq;
      this.qrError = false;

      if (
        !this.googleform ||
        !this.googleform.name ||
        !this.googleform.secretKey
      ) {
        this.qrLoading = false;
        this.revokeQrObjectUrl();
        this.newSrc = "";
        return;
      }

      this.qrLoading = true;
      try {
        const res = await GoogleQr(
          this.googleform.name,
          this.googleform.secretKey,
          getAccessTokenFromAuthData(this.googleform),
        );
        if (requestSeq !== this.qrRequestSeq) return;

        const contentType =
          (res.headers && res.headers["content-type"]) || "image/png";
        const blob =
          res.data instanceof Blob
            ? res.data
            : new Blob([res.data], { type: contentType });

        this.revokeQrObjectUrl();
        this.qrObjectUrl = window.URL.createObjectURL(blob);
        this.newSrc = this.qrObjectUrl;
      } catch (e) {
        if (requestSeq !== this.qrRequestSeq) return;
        this.revokeQrObjectUrl();
        this.newSrc = "";
        this.qrError = true;
      } finally {
        if (requestSeq === this.qrRequestSeq) {
          this.qrLoading = false;
          this.reloadIcons();
        }
      }
    },
    handleQrLoadError() {
      this.revokeQrObjectUrl();
      this.newSrc = "";
      this.qrError = true;
    },
    sureverCode() {
      if (!this.verCode || this.verCode.length !== 6) {
        this.$tbMessage({ type: "warning", text: this.$t("登录失败") });
        return;
      }
      bindingverCode(
        this.verCode,
        this.googleform.secretKey,
        getAccessTokenFromAuthData(this.googleform),
      )
        .then((res) => {
          if (res.code == 200) {
            this.$tbMessage({ type: "success", text: this.$t("login.bindingModule.verification.success"), });
            this.$emit("bindingSuccess");
          }
        })
        .catch(() => {
          this.$tbMessage({
            type: "warning",
            text: this.$t("login.bindingModule.verification.timeExpired"),
          });
          setTimeout(() => {
            this.$emit("handleClose1");
            clearLocalStorageExceptUserGuides();
          }, 1500);
        });
    },
  },
};
</script>

<style lang="less" scoped>
.bind-modal {
  width: 100%;
}

.bind-modal__stack {
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.bind-modal__panel {
  position: relative;
  border-radius: 16px;
  background: #151a27;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
}

.bind-modal__panel--top {
  padding: 24px;
}

.bind-modal__panel--bottom {
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.bind-modal__close {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 2;
  padding: 8px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  transition: color 0.2s ease, background-color 0.2s ease;
}

.bind-modal__close i {
  width: 20px;
  height: 20px;
}

.bind-modal__close:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.1);
}

.bind-modal__title {
  margin: 0 0 32px;
  color: #fff;
  font-size: 20px;
  line-height: 28px;
  font-weight: 700;
}

.bind-modal__desc {
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  color: #94a3b8;
  font-size: 14px;
  line-height: 1.5;
}

.bind-modal__help {
  margin-left: 4px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #3b82f6;
  font-size: 14px;
  line-height: 1.5;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s ease;
}

.bind-modal__help:hover {
  color: #2563eb;
}

.bind-modal__secret {
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 24px;
  border-radius: 8px;
  background: rgba(10, 15, 29, 0.6);
}

.bind-modal__qr-shell {
  padding: 10px;
  flex-shrink: 0;
  border-radius: 8px;
  background: #fff;
  box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.06);
}

.bind-modal__qr {
  width: 94px;
  height: 94px;
  display: block;
  border-radius: 6px;
}

.bind-modal__qr-state {
  width: 94px;
  height: 94px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 6px;
  background: #f8fafc;
  color: #64748b;
  font-size: 12px;
}

.bind-modal__qr-retry {
  cursor: pointer;
  font-family: inherit;
}

.bind-modal__qr-retry:hover {
  color: #2563eb;
}

.bind-modal__qr-loading {
  width: 22px;
  height: 22px;
  animation: bind-spin 1s linear infinite;
}

.bind-modal__secret-body {
  flex: 1;
}

.bind-modal__secret-label {
  margin-bottom: 8px;
  color: #94a3b8;
  font-size: 14px;
  line-height: 20px;
}

.bind-modal__secret-code {
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.02);
}

.bind-modal__secret-code span {
  color: #fff;
  font-size: 16px;
  line-height: 24px;
  font-weight: 700;
  letter-spacing: 0.15em;
  font-family: SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace;
}

.bind-modal__steps {
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.bind-step {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 8px;
  background: rgba(10, 15, 29, 0.6);
}

.bind-step__head {
  height: 54px;
  padding: 0 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.bind-step__index {
  color: rgba(59, 130, 246, 0.7);
  font-size: 28px;
  line-height: 1;
  font-weight: 900;
  font-family: SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace;
}

.bind-step__text {
  color: #94a3b8;
  font-size: 12px;
  line-height: 1.35;
}

.bind-step__text-strong {
  color: #fff;
  margin-left: 2px;
}

.bind-step__body {
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(0, 0, 0, 0.2) 100%
  );
}

.bind-step__scan {
  position: relative;
  width: 120px;
  height: 84px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 8px 8px 0 0;
  background: rgba(255, 255, 255, 0.05);
}

.bind-step__scan-icon {
  width: 40px;
  height: 40px;
  margin-bottom: 4px;
  color: rgba(255, 255, 255, 0.2);
}

.bind-step__scan-bar {
  width: 64px;
  height: 4px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
}

.bind-step__scan-line {
  position: absolute;
  top: 16px;
  width: 80%;
  height: 2px;
  background: rgba(59, 130, 246, 0.8);
  box-shadow: 0 0 8px rgba(59, 130, 246, 0.8);
  animation: bind-scan 2s ease-in-out infinite alternate;
}

.bind-token {
  width: 100%;
  height: 84px;
  padding: 12px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border-radius: 8px;
  background: #1a1d2e;
  box-shadow: 0 10px 18px rgba(0, 0, 0, 0.28);
}

.bind-token__top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  opacity: 0.6;
  color: #fff;
  font-size: 10px;
  line-height: 1;
  font-weight: 500;
}

.bind-token__more,
.bind-token__copy {
  width: 12px;
  height: 12px;
}

.bind-token__code {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #56719e;
  font-size: 22px;
  line-height: 1;
  font-weight: 700;
  letter-spacing: 0.14em;
  font-family: SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace;
}

.bind-token__copy {
  color: rgba(255, 255, 255, 0.2);
}

.bind-token__progress {
  width: 100%;
  height: 4px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
}

.bind-token__progress-bar {
  width: 100%;
  height: 100%;
  border-radius: 999px;
  background: rgba(86, 113, 158, 0.3);
  animation: bind-progress 30s linear infinite;
}

.bind-modal__input-wrap {
  position: relative;
  flex: 1;
}

.bind-modal__panel--bottom ::v-deep(.el-input) {
  width: 100%;
}

.bind-modal__panel--bottom ::v-deep(.el-input__inner) {
  height: 52px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  text-align: center;
  font-size: 24px;
  line-height: 52px;
  font-weight: 400;
  letter-spacing: 0.3em;
  // box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.2);
  font-family: SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace;
}

.bind-modal__panel--bottom ::v-deep(.el-input__inner::placeholder) {
  color: rgba(148, 163, 184, 0.5);
  font-size: 14px;
  letter-spacing: 0;
  font-family: "PingFang SC", sans-serif;
}

.bind-modal__panel--bottom ::v-deep(.el-input__inner:focus) {
  border-color: rgba(59, 130, 246, 0.5);
}

.bind-modal__input-clear {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #a2a9b6;
  transition: color 0.2s ease;
  z-index: 2;

  &:hover {
    color: #fff;
  }
}

.bind-modal__confirm {
  width: 140px;
  height: 52px;
  border: 0;
  border-radius: 12px;
  background: #3b82f6;
  color: #fff;
  font-size: 16px;
  line-height: 52px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.2s ease, opacity 0.2s ease;
  box-shadow: 0 0 0 rgba(0, 0, 0, 0), 0 10px 18px rgba(59, 130, 246, 0.2);
}

.bind-modal__confirm:hover {
  background: #2563eb;
}

.bind-modal__confirm:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@keyframes bind-progress {
  0% {
    width: 100%;
  }
  100% {
    width: 0%;
  }
}

@keyframes bind-scan {
  0% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(30px);
  }
}

@keyframes bind-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  .bind-modal__stack {
    width: 100%;
  }

  .bind-modal__panel--top,
  .bind-modal__panel--bottom {
    padding: 18px;
  }

  .bind-modal__secret,
  .bind-modal__panel--bottom {
    flex-direction: column;
    align-items: stretch;
  }

  .bind-modal__steps {
    grid-template-columns: 1fr;
  }

  .bind-modal__confirm {
    width: 100%;
  }
}
</style>
